import { z } from 'zod';
import { colorSchema, gradeSchema, idSchema, isoDateTimeSchema, localeSchema, mmSchema, } from './common.js';
import { assetSchema } from './assets.js';
import { boardInteractionSchema } from './items.js';
import { fontRefSchema } from './fonts.js';
import { pageFrameSchema, pageSchema } from './page.js';
/** Aktuelle Schemaversion; Erhöhung nur zusammen mit einer Migration. */
export const CURRENT_SCHEMA_VERSION = 1;
/** Papierformate in mm (Hochformat). */
export const PAGE_SIZES = {
    A4: { width: 210, height: 297 },
    A5: { width: 148, height: 210 },
    A3: { width: 297, height: 420 },
};
export const metaSchema = z.object({
    title: z.string().min(1, { error: 'Das Dokument braucht einen Titel' }),
    description: z.string().optional(),
    subject: z.string().optional(),
    grades: z.array(gradeSchema).optional(),
    tags: z.array(z.string()).default([]),
    authorId: idSchema,
    createdAt: isoDateTimeSchema,
    updatedAt: isoDateTimeSchema,
    /** Vorlage, aus der dieses Dokument entstanden ist. */
    templateOf: idSchema.optional(),
});
export const placeValueColorsSchema = z.object({
    ones: colorSchema,
    tens: colorSchema,
    hundreds: colorSchema,
    thousands: colorSchema,
});
export const pageSetupSchema = z.object({
    output: z.enum(['paper', 'digital', 'board']),
    size: z.object({ width: mmSchema, height: mmSchema }),
    orientation: z.enum(['portrait', 'landscape']),
    margins: z.object({ top: mmSchema, right: mmSchema, bottom: mmSchema, left: mmSchema }),
    /** Ränder auf geraden Seiten spiegeln. */
    duplex: z.boolean(),
    boardAspect: z.enum(['16:9', '4:3']).optional(),
    /** Faktor für alle Linienstärken, 1,0 = Standard. */
    lineWeight: z.number().positive(),
    /** `null` = Lösungen nicht einfärben. */
    solutionColor: colorSchema.nullable(),
    placeValueColors: placeValueColorsSchema.nullable(),
    /** Stellenwertfarben auf alle Zahlen anwenden (Phase 6.4). */
    placeValueAll: z.boolean().optional(),
    locale: localeSchema,
    handDrawn: z.boolean(),
    grid: z.object({ step: mmSchema, visible: z.boolean() }),
});
export const documentStylesSchema = z.object({
    defaultFont: fontRefSchema,
    /** Schriftgröße in pt. */
    defaultFontSize: z.number().positive(),
    textColor: colorSchema,
});
export const groupSchema = z.object({
    id: idSchema,
    memberIds: z.array(idSchema).min(1),
    board: boardInteractionSchema.optional(),
});
export const worksheetDocumentSchema = z.object({
    schemaVersion: z.literal(CURRENT_SCHEMA_VERSION, {
        error: `Es wird schemaVersion ${CURRENT_SCHEMA_VERSION} erwartet; ältere Dokumente zuerst migrieren`,
    }),
    id: idSchema,
    meta: metaSchema,
    setup: pageSetupSchema,
    styles: documentStylesSchema,
    /** Wiederverwendbare Seitenrahmen. */
    frames: z.record(idSchema, pageFrameSchema).default({}),
    assets: z.record(idSchema, assetSchema).default({}),
    /** Reihenfolge = Seitenfolge. */
    pages: z.array(pageSchema).min(1, { error: 'Ein Dokument braucht mindestens eine Seite' }),
    /** Flach, keine verschachtelten Gruppen. */
    groups: z.record(idSchema, groupSchema).default({}),
    /** Basis-Seed des Dokuments; mit ihm sind alle Generatoren reproduzierbar. */
    seed: z.number().int().nonnegative(),
    language: z.object({
        /** z. B. { "Ameise": "A=mei=se" } */
        syllableOverrides: z.record(z.string(), z.string()).default({}),
    }),
});
export const submissionSchema = z.object({
    documentId: idSchema,
    documentRevision: z.number().int().nonnegative(),
    /** Pseudonym statt Klarname (Datenschutz, Rahmenbedingung 5). */
    pseudonymId: z.string().min(1),
    answers: z.record(z.string(), z.union([z.string(), z.number(), z.boolean(), z.null()])),
    attempts: z.record(z.string(), z.number().int().nonnegative()),
    completedAt: isoDateTimeSchema.nullable(),
});
//# sourceMappingURL=document.js.map